import apiClient, { apiRequest } from "@services/apiClient";
import aiEndpoints from "@services/ai/aiEndpoints";

function normalizeVoice(voice = {}) {
  return {
    id: String(voice?.id || voice?.voice || voice?.name || ""),
    name: String(voice?.name || ""),
    description: String(voice?.description || ""),
    summary: String(voice?.summary || voice?.description || ""),
    gender: String(voice?.gender || ""),
    pitch: String(voice?.pitch || ""),
    persona: String(voice?.persona || ""),
    useCase: String(voice?.use_case || voice?.useCase || voice?.context || ""),
  };
}

function normalizeVoices(body = {}) {
  const voices = (Array.isArray(body?.voices) ? body.voices : [])
    .filter((voice) => voice && typeof voice === "object")
    .map(normalizeVoice)
    .filter((voice) => voice.id && voice.name)
    .sort((first, second) =>
      first.name.localeCompare(second.name, "en", { sensitivity: "base" }),
    );
  const groups = (Array.isArray(body?.groups) ? body.groups : [])
    .filter((group) => group && typeof group === "object")
    .map((group) => ({
      id: String(group?.id || group?.name || ""),
      name: String(group?.name || group?.id || ""),
      total: Number(group?.total || group?.voices?.length || 0),
      voices: (Array.isArray(group?.voices) ? group.voices : [])
        .map(normalizeVoice)
        .filter((voice) => voice.id && voice.name),
    }))
    .filter((group) => group.id && group.name);
  return {
    model: String(body?.model || ""),
    voices,
    groups,
  };
}

function normalizeCatalogOptions(value) {
  return (Array.isArray(value) ? value : [])
    .map((item) => {
      if (item && typeof item === "object") {
        const id = String(item?.id || "").trim();
        const name = String(item?.name || item?.label || id).trim();
        return id ? { id, name } : null;
      }
      const id = String(item || "").trim();
      return id ? { id, name: id } : null;
    })
    .filter(Boolean)
    .sort((first, second) =>
      first.name.localeCompare(second.name, "en", { sensitivity: "base" }),
    );
}

function normalizeVoicesCatalog(body = {}) {
  const filters = body?.filters || body || {};
  return {
    model: String(body?.model || ""),
    source: String(body?.source || ""),
    cached: Boolean(body?.cached),
    language: normalizeCatalogOptions(filters?.language),
    gender: normalizeCatalogOptions(filters?.gender),
    pitch: normalizeCatalogOptions(filters?.pitch),
    style: normalizeCatalogOptions(filters?.style),
    useCase: normalizeCatalogOptions(filters?.use_case),
    vocalTags: Array.isArray(body?.vocal_tags)
      ? body.vocal_tags
          .map((tag) => String(tag || "").trim())
          .filter(Boolean)
      : [],
  };
}

export async function listAiVoicesRequest() {
  const response = await apiRequest(aiEndpoints.voices);
  return {
    ...response,
    body: normalizeVoices(response?.body),
  };
}

export async function listAiVoicesCatalogRequest() {
  const response = await apiRequest(aiEndpoints.voicesCatalog);
  return {
    ...response,
    body: normalizeVoicesCatalog(response?.body),
  };
}

export async function searchAiVoicesRequest(filters = {}) {
  const optionalArray = (value) => {
    const normalized = String(value || "").trim();
    return normalized ? [normalized] : [];
  };
  const body = {
    ...(String(filters.search || "").trim()
      ? { search: String(filters.search).trim() }
      : {}),
    ...(optionalArray(filters.language).length
      ? { language: optionalArray(filters.language) }
      : {}),
    ...(optionalArray(filters.gender).length
      ? { gender: optionalArray(filters.gender) }
      : {}),
    ...(optionalArray(filters.pitch).length
      ? { pitch: optionalArray(filters.pitch) }
      : {}),
    ...(optionalArray(filters.style).length
      ? { style: optionalArray(filters.style) }
      : {}),
    ...(optionalArray(filters.useCase).length
      ? { use_case: optionalArray(filters.useCase) }
      : {}),
    ...(String(filters.pageToken || "").trim()
      ? { page_token: String(filters.pageToken).trim() }
      : {}),
  };
  const response = await apiRequest(aiEndpoints.voicesSearch, {
    method: "POST",
    body,
  });
  return {
    ...response,
    body: {
      ...normalizeVoices(response?.body),
      nextPageToken: String(response?.body?.next_page_token || ""),
    },
  };
}

export function previewAiVoiceRequest(voice) {
  return apiRequest(aiEndpoints.voicePreview, {
    method: "POST",
    body: { voice: String(voice || "") },
  });
}

export function listAiPromptsRequest() {
  return apiRequest(aiEndpoints.aiPrompts);
}

export function updateAiPromptRequest(promptKey, promptText) {
  const key = String(promptKey || "").trim();
  if (!key) throw new Error("An AI prompt key is required.");
  return apiRequest(`${aiEndpoints.aiPrompts}/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: { prompt_text: String(promptText || "") },
  });
}

export async function generateTextToSpeechRequest(payload = {}) {
  const id = String(payload?.id || "");
  if (!id) throw new Error("A point ID is required to generate audio.");
  const speakers = Array.isArray(payload?.speakers)
    ? payload.speakers
        .filter((speaker) => speaker && speaker.speaker && (speaker.id || speaker.voice))
        .map((speaker) => ({
          speaker: String(speaker.speaker),
          id: String(speaker.id || speaker.voice),
        }))
    : [];
  let streamedPoint = null;
  const response = await generateTextToSpeechStream([
    {
      id,
      text: String(payload?.text || ""),
      ...(speakers.length
        ? { speakers }
        : { voice: String(payload?.voice || "") }),
    },
  ], ({ event, data }) => {
    if (event === "point" && String(data?.id || data?.point?.id || "") === id) {
      streamedPoint = {
        ...data.point,
        error: data.status === "error" || Boolean(data.point?.error),
        message: data.point?.message || data.message,
      };
    }
  });
  const result = streamedPoint || response?.body?.data?.find((row) => String(row?.id) === id);
  if (response?.error || result?.error) {
    throw new Error(result?.message || response?.message || "Could not generate audio.");
  }
  if (!result?.multimedia?.id || !result?.multimedia?.url) {
    throw new Error("The AI service did not return an audio file.");
  }
  return { ...response, body: { ...response?.body, multimedia: result.multimedia } };
}

export function generateRouteModesRequest(payload = {}) {
  return apiRequest(aiEndpoints.generateRouteModes, {
    method: "POST",
    body: payload,
  });
}

function parseSseFrame(frame) {
  let event = "message";
  const data = [];
  for (const line of frame.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    const separator = line.indexOf(":");
    const field = separator < 0 ? line : line.slice(0, separator);
    const value = separator < 0 ? "" : line.slice(separator + 1).replace(/^ /, "");
    if (field === "event") event = value || "message";
    if (field === "data") data.push(value);
  }
  if (!data.length) return null;
  return { event, data: data.join("\n") };
}

function parseSseData(frame) {
  const parsed = parseSseFrame(frame);
  if (!parsed) return null;
  try {
    return { event: parsed.event, data: JSON.parse(parsed.data) };
  } catch {
    throw new Error(`The AI stream sent invalid data for event '${parsed.event}'.`);
  }
}

async function generateRouteModesStream(endpointPath, payload = {}, onEvent) {
  const baseUrl = String(apiClient.defaults.baseURL || "").replace(/\/+$/, "");
  const endpoint = String(endpointPath || "").replace(/^\/+/, "");
  const response = await fetch(`${baseUrl}/${endpoint}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify(payload),
  });

  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  if (!response.ok || !contentType.includes("text/event-stream")) {
    let body = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    if (!response.ok || body?.error) {
      throw new Error(body?.message || `AI request failed with status ${response.status}.`);
    }
    if (body) return body;
    throw new Error("The AI server did not return an event stream.");
  }

  if (!response.body?.getReader) {
    throw new Error("This browser cannot read the AI response stream.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completedResponse = null;

  async function dispatchFrame(frame) {
    const parsed = parseSseData(frame);
    if (!parsed) return;
    if (parsed.event === "error") {
      reader.cancel().catch(() => {});
      throw new Error(parsed.data?.message || "The AI stream failed while processing route points.");
    }
    await onEvent?.(parsed);
    if (parsed.event === "complete") completedResponse = parsed.data;
  }

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    buffer = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      await dispatchFrame(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf("\n\n");
    }
    if (done) break;
  }
  if (buffer.trim()) await dispatchFrame(buffer);
  if (!completedResponse) {
    throw new Error("The AI stream ended before sending the complete event.");
  }
  return completedResponse;
}

export function generateRouteModesContentStream(payload = {}, onEvent) {
  return generateRouteModesStream(aiEndpoints.generateRouteModesContent, payload, onEvent);
}

export function generateRouteModesDirectionsStream(payload = {}, onEvent) {
  return generateRouteModesStream(aiEndpoints.generateRouteModesDirections, payload, onEvent);
}

export function generateRouteModesImagesStream(payload = {}, onEvent) {
  return generateRouteModesStream(aiEndpoints.generateRouteModesImages, payload, onEvent);
}

export function generateRouteModesMusicStream(payload = {}, onEvent) {
  return generateRouteModesStream(aiEndpoints.generateRouteModesMusic, payload, onEvent);
}

export function mixRouteModesAudioStream(payload = {}, onEvent) {
  return generateRouteModesStream(aiEndpoints.mixRouteModesAudio, payload, onEvent);
}

export function translateRouteModesStream(payload = {}, onEvent) {
  return generateRouteModesStream(aiEndpoints.translateRouteModes, payload, onEvent);
}

export function translateGenericStream(payload = {}, onEvent) {
  return generateRouteModesStream(aiEndpoints.translateGeneric, payload, onEvent);
}

export function generateTextToSpeechStream(payload = [], onEvent) {
  const body = Array.isArray(payload) ? { data: payload } : payload;
  return generateRouteModesStream(aiEndpoints.tts, body, onEvent);
}

export function generateRouteModesDirectionsRequest(payload = {}) {
  return apiRequest(aiEndpoints.generateRouteModesDirections, {
    method: "POST",
    body: payload,
  });
}

export function generateRouteModesParkingRequest(payload = {}) {
  return apiRequest(aiEndpoints.generateRouteModesParking, {
    method: "POST",
    body: payload,
  });
}
